import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "@/components/icons";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getErrorMessage } from "@/lib/errors";
import { ErrorState } from "@/components/ListState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const SettingsEntities = () => {
  const { canWrite } = useAuth();
  const writable = canWrite();
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const EMPTY_INV = { statutaire_naam: "", adres: "", postcode: "", plaats: "", kvk: "", btw_nummer: "", factuur_email: "" };
  const [inv, setInv] = useState(EMPTY_INV);
  const [error, setError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const { data } = await api.get("/entities");
      setEntities(data);
    } catch (err) {
      setLoadError(getErrorMessage(err, "Kostenentiteiten konden niet geladen worden."));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setName(""); setInv(EMPTY_INV); setError(""); setDialogOpen(true); };
  const openEdit = (e) => { setEditing(e); setName(e.name); setInv({ statutaire_naam: e.statutaire_naam || "", adres: e.adres || "", postcode: e.postcode || "", plaats: e.plaats || "", kvk: e.kvk || "", btw_nummer: e.btw_nummer || "", factuur_email: e.factuur_email || "" }); setError(""); setDialogOpen(true); };

  const submit = async () => {
    setError("");
    try {
      if (editing) {
        await api.put(`/entities/${editing.entity_id}`, { name, ...inv });
        toast.success("Entiteit bijgewerkt");
      } else {
        await api.post("/entities", { name, ...inv });
        toast.success("Entiteit aangemaakt");
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      setError(getErrorMessage(err, "Opslaan mislukt."));
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/entities/${deleteTarget.entity_id}`);
      toast.success("Entiteit verwijderd");
      setDeleteTarget(null);
      await load();
    } catch (err) {
      toast.error(getErrorMessage(err, "Verwijderen mislukt."));
      setDeleteTarget(null);
    }
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-body text-muted-foreground">{entities.length} kostenentiteiten</p>
        {writable && (
          <Button data-testid="cost-entities-create-button" onClick={openCreate}>
            <Plus size={18} className="mr-2" /> Entiteit toevoegen
          </Button>
        )}
      </div>

      <Card>
        {loadError ? (
          <ErrorState testid="cost-entities-load-error" message={loadError} onRetry={load} className="border-0" />
        ) : (
        <div>
          <Table data-testid="cost-entities-table">
            <TableHeader>
              <TableRow>
                <TableHead>Naam</TableHead>
                <TableHead>Code</TableHead>
                <TableHead className="w-[120px] text-right">Acties</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={3} className="py-8 text-center text-body text-muted-foreground">Laden...</TableCell></TableRow>
              )}
              {!loading && entities.map((e) => (
                <TableRow key={e.entity_id} data-testid={`entity-row-${e.entity_id}`}>
                  <TableCell className="font-medium text-foreground">{e.name}</TableCell>
                  <TableCell className="font-mono text-data text-muted-foreground">{e.entity_id}</TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-1">
                      {writable ? (
                        <>
                          <Button variant="ghost" size="icon" data-testid={`cost-entity-row-edit-button-${e.entity_id}`} onClick={() => openEdit(e)} title="Bewerken">
                            <Pencil size={18} />
                          </Button>
                          <Button variant="ghost" size="icon" data-testid={`cost-entity-row-delete-button-${e.entity_id}`} onClick={() => setDeleteTarget(e)} title="Verwijderen">
                            <Trash2 size={18} />
                          </Button>
                        </>
                      ) : (
                        <span className="text-data text-muted-foreground">Geen rechten</span>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        )}
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent srDescription="Naam van de kostenentiteit waaraan kosten worden toegerekend.">
          <DialogHeader>
            <DialogTitle>{editing ? "Entiteit bewerken" : "Entiteit toevoegen"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="ent-name">Naam</Label>
              <Input id="ent-name" data-testid="cost-entity-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="bijv. Techniek Beheer BV" />
              {editing && <p className="font-mono text-data text-muted-foreground">Code: {editing.entity_id}</p>}
            </div>
            <div className="rounded border border-lijn-zacht p-3 space-y-3" data-testid="entity-invoice-fields">
              <p className="text-data text-muted-foreground">Factuurgegevens (nodig om te kunnen factureren)</p>
              <div className="space-y-1.5">
                <Label htmlFor="ent-stat">Statutaire naam</Label>
                <Input id="ent-stat" data-testid="entity-statutaire-naam-input" value={inv.statutaire_naam} onChange={(e) => setInv((f) => ({ ...f, statutaire_naam: e.target.value }))} placeholder="bijv. Techniek Beheer B.V." />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ent-adres">Adres</Label>
                <Input id="ent-adres" data-testid="entity-adres-input" value={inv.adres} onChange={(e) => setInv((f) => ({ ...f, adres: e.target.value }))} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="ent-pc">Postcode</Label>
                  <Input id="ent-pc" data-testid="entity-postcode-input" value={inv.postcode} onChange={(e) => setInv((f) => ({ ...f, postcode: e.target.value }))} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ent-plaats">Plaats</Label>
                  <Input id="ent-plaats" data-testid="entity-plaats-input" value={inv.plaats} onChange={(e) => setInv((f) => ({ ...f, plaats: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="ent-kvk">KvK</Label>
                  <Input id="ent-kvk" data-testid="entity-kvk-input" value={inv.kvk} onChange={(e) => setInv((f) => ({ ...f, kvk: e.target.value }))} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ent-btw">BTW-nummer</Label>
                  <Input id="ent-btw" data-testid="entity-btw-input" value={inv.btw_nummer} onChange={(e) => setInv((f) => ({ ...f, btw_nummer: e.target.value }))} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ent-mail">Factuur-e-mailadres</Label>
                <Input id="ent-mail" type="email" data-testid="entity-factuur-email-input" value={inv.factuur_email} onChange={(e) => setInv((f) => ({ ...f, factuur_email: e.target.value }))} />
              </div>
            </div>
            {error && <p className="text-data text-destructive">{error}</p>}
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDialogOpen(false)}>Annuleren</Button>
            <Button data-testid="cost-entity-submit-button" onClick={submit}>Opslaan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Entiteit verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je "{deleteTarget?.name}" wilt verwijderen? Dit kan niet ongedaan gemaakt worden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction data-testid="cost-entity-delete-confirm" onClick={confirmDelete}>Verwijderen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
